import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { SETTINGS_PAGE } from './settings-shared';

/**
 * The phone layout's share-sheet action, sharing the caller's own attributed invite link.
 *
 * It must be the SAME link `InviteLinkRow` shows beside it: any install link without the sharer's id in
 * it credits nobody, and looks identical to one that works — so a recipient who receives it can never be
 * attributed and the sharer only discovers that by never being paid. For the same reason an absent link
 * is refused rather than substituted.
 *
 * `navigator.share` is the real share sheet where the browser has one; every browser that doesn't falls
 * back to copying the same message to the clipboard, so the action always does something. The return
 * value tells the caller which happened, so it can say "Copied".
 */
export type ShareAppOutcome = 'shared' | 'copied' | 'failed';

export function useShareApp(shareLink: string): () => Promise<ShareAppOutcome> {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();

  return async () => {
    if (!shareLink) return 'failed';
    logPageClickBi({ ...SETTINGS_PAGE, button_name: 'share_app' });
    const text = t('settings.share.message', { productName });

    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: productName, text, url: shareLink });
        return 'shared';
      } catch {
        // A dismissed share sheet rejects exactly like a failed one, so fall through to the
        // clipboard rather than reporting an error the user caused by cancelling.
      }
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(`${text} ${shareLink}`);
        return 'copied';
      } catch {
        return 'failed';
      }
    }
    return 'failed';
  };
}
